import { useEffect, useMemo, useState } from 'react'
import { isPastEvent, sortByStartDesc, type EventRecord } from '@romain130492/topchopsticks'
import { api } from '../lib/api'
import { useI18n } from '../hooks/useI18n'
import { useEventBooking } from '../hooks/useEventBooking'
import EventCard from '../components/EventCard'
import GuestBanner from '../components/GuestBanner'
import LoadingState from '../components/LoadingState'
import { shareEvent } from '../lib/share'
import { usePullToRefresh } from '../hooks/usePullToRefresh'

/**
 * Social Dining — one scroll: curated tables, then community tables.
 *
 * Past dinners are kept OUT of the feed and folded behind one quiet line at the
 * bottom. The API returns everything ordered by start_at ascending, so without
 * this the first thing on screen is a dinner nobody can attend.
 */
export default function EventsPage({
  registered, onRegister, onOpenEvent
}: {
  registered: boolean
  onRegister: (reason?: string) => void
  onOpenEvent: (eventId: string) => void
}) {
  const { t, L } = useI18n()
  const [curated, setCurated] = useState<EventRecord[] | null>(null)
  const [community, setCommunity] = useState<EventRecord[] | null>(null)
  const [showPast, setShowPast] = useState(false)

  const load = () => Promise.all([
    api.getEvents({ source: 'curated' }).then(setCurated).catch(() => setCurated([])),
    api.getEvents({ source: 'community' }).then(setCommunity).catch(() => setCommunity([]))
  ])

  const booking = useEventBooking({ onChanged: load })
  useEffect(() => { load() }, [])

  // Pull down to reload, like onPullDownRefresh in the mini program. Reloads
  // the SEATS too — a refresh that showed new events but stale booking state
  // would be worse than none.
  const pullToRefresh = usePullToRefresh(() => Promise.all([load(), booking.reload()]))

  const loading = curated === null || community === null

  const { curatedUpcoming, communityUpcoming, past } = useMemo(() => {
    const split = (events: EventRecord[] | null) => {
      const list = events || []
      return {
        upcoming: list.filter((event) => !isPastEvent(event)),
        past: sortByStartDesc(list.filter((event) => isPastEvent(event)))
      }
    }
    const c = split(curated)
    const m = split(community)
    return { curatedUpcoming: c.upcoming, communityUpcoming: m.upcoming, past: [...c.past, ...m.past] }
  }, [curated, community])

  const cardProps = (event: EventRecord) => ({
    event,
    booked: Boolean(booking.seatFor(event.id)),
    busy: booking.busyEventId === event.id,
    seatsReady: booking.ready,
    onReserve: booking.reserve,
    onCancel: booking.cancel,
    attendeesKey: booking.version,
    registered,
    onRegister,
    onOpen: (e: EventRecord) => onOpenEvent(e.id),
    onShare: (e: EventRecord) => shareEvent(e, {
      title: L(e.title as never),
      desc: L(e.description as never),
      copied: t('ev.linkCopied'),
      hint: t('ev.shareHint')
    })
  })

  return (
    <div>
      {/* Follows the finger, then becomes a spinner. Height 0 at rest so it
          takes no space and shifts nothing. */}
      <div
        className="flex items-end justify-center overflow-hidden text-2xs text-gold"
        style={{
          height: pullToRefresh.refreshing ? 34 : pullToRefresh.pull,
          transition: pullToRefresh.pull ? 'none' : 'height 180ms ease-out'
        }}
      >
        {pullToRefresh.refreshing
          ? <span className="mb-2 inline-block h-5 w-5 animate-[spin_0.7s_linear_infinite] rounded-full border-2 border-gold border-t-transparent" />
          : <span className="mb-2">{pullToRefresh.active ? t('ev.releaseToRefresh') : t('ev.pullToRefresh')}</span>}
      </div>

      {!registered ? <GuestBanner variant="events" onRegister={onRegister} /> : null}

      {loading ? <LoadingState /> : (
        <>
          <SectionRule label={t('ev.curatedHeading')} />
          {curatedUpcoming.length
            ? curatedUpcoming.map((event) => <EventCard key={event.id} {...cardProps(event)} />)
            : <Empty text={t('ev.emptyCurated')} />}

          <SectionRule label={t('ev.communityHeading')} />
          {communityUpcoming.length
            ? communityUpcoming.map((event) => <EventCard key={event.id} {...cardProps(event)} />)
            : <Empty text={t('ev.emptyCommunity')} />}

          {past.length ? (
            <div className="mt-4">
              {/* Deliberately quiet: a text line, not a button, so it never
                  competes with the primary actions above it. */}
              <button
                type="button"
                onClick={() => setShowPast((open) => !open)}
                className="w-full py-3 text-center text-sm text-muted underline"
              >
                {showPast ? t('ev.hidePast') : t('ev.seePast')} · {past.length}
              </button>
              {showPast ? (
                <div className="mt-2 opacity-70">
                  {/* No BOOKING handlers — a finished dinner has nothing to
                      reserve. But it still has a page and is still shareable,
                      and omitting those was why triple-tap and the share icon
                      did nothing on past cards. */}
                  {past.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      registered={registered}
                      onOpen={(e) => onOpenEvent(e.id)}
                      onShare={(e) => shareEvent(e, {
                        title: L(e.title as never),
                        desc: L(e.description as never),
                        copied: t('ev.linkCopied'),
                        hint: t('ev.shareHint')
                      })}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

const SectionRule = ({ label }: { label: string }) => (
  <div className="my-4 flex items-center gap-3">
    <span className="h-px flex-1 bg-gold-dim" />
    <span className="text-2xs uppercase tracking-kicker text-gold">{label}</span>
    <span className="h-px flex-1 bg-gold-dim" />
  </div>
)

const Empty = ({ text }: { text: string }) => (
  <p className="py-6 text-center text-13 text-muted">{text}</p>
)
